import Upload from 'gcs-browser-upload';

import {sleep} from 'common/util/dom';
import {formatPercent} from 'common/util/index';
import {apiClient} from 'common/util/api-client';
import {reporter} from 'common/report/error-reporting';
import {getNow} from 'common/util/timing';

// A couple of Uber projects require larger than normal lookup tables
// TODO potentially move to a config on the backend
const LOOKUP_TABLE_SIZE_PROJECT_OVERRIDES = {
  2731255: 2 * 1024,
  2764429: 2 * 1024,
};

// These functions are separate from existing LookupTablesClient because that class
// extends deprecated BaseClient

function getLookupTablesBaseUrl(projectId: number, workspaceId: number) {
  const workspaceUrlPath = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
  return `/api/app/${workspaceUrlPath}/data-definitions/lookup-tables`;
}

function getLookupTableUploadUrl(projectId: number, workspaceId: number, contentType: string) {
  const url = `${getLookupTablesBaseUrl(projectId, workspaceId)}/upload-url/`;
  return apiClient.get<{key: string; path: string; url: string}>(url, {'content-type': contentType});
}

type ProcessLookupTablePendingResults = {uploadId: number};
type ProcessLookupTableErrorResults = {error: string};
type ProcessLookupTableSuccessResults = {
  id: string;
  columns: number;
  deduped: number;
  imported: number;
  file: number;
};
type ProcessLookupTableCompletedResults = ProcessLookupTableErrorResults | ProcessLookupTableSuccessResults;
/** Use `'uploadId' in result` to narrow type (https://www.typescriptlang.org/docs/handbook/advanced-types.html#using-the-in-operator) */
type ProcessLookupTableResults = ProcessLookupTablePendingResults | ProcessLookupTableCompletedResults;

type LookupTableUploadStatusResults =
  | {uploadStatus: `SUCCESS`; result: ProcessLookupTableCompletedResults}
  | {uploadStatus: `FAILURE`};

function processLookupTable(
  projectId: number,
  workspaceId: number,
  key: string,
  name: string,
  path: string,
  dataGroupId: string | null = null,
  deferMarkReady = false,
) {
  const formData = new FormData();
  formData.append(`key`, key);
  formData.append(`name`, name);
  formData.append(`path`, path);
  if (dataGroupId !== null) {
    formData.append(`data-group-id`, dataGroupId);
  }
  if (deferMarkReady) {
    formData.append(`defer-mark-ready`, `true`);
  }

  return apiClient.post<ProcessLookupTableResults>(getLookupTablesBaseUrl(projectId, workspaceId), formData);
}

function getLookupTableUploadStatus(projectId: number, workspaceId: number, uploadId: number) {
  const url = `${getLookupTablesBaseUrl(projectId, workspaceId)}/upload-status/`;
  return apiClient.get<LookupTableUploadStatusResults>(url, {'upload-id': uploadId});
}

type UploadSuccessResult = {
  succeeded: true;
  rowsUploaded: number;
  rowsImported: number;
  numColumns: number;
  dataGroupId: string;
  secondsToGetUrl: number;
  secondsToUpload: number;
  secondsToProcess: number;
  secondsTotal: number;
  droppedRowsWarningMessage?: string;
  warningReason?: string;
};
type UploadFailedResult = {succeeded: false; errorMessage: string};

export async function uploadLookupTable({
  file,
  projectId,
  workspaceId,
  onUploadProgress,
  onProcessingStarted,
  deferMarkReady = false,
  dataGroupId = null,
  groupKey = null,
}: {
  file: File;
  projectId: number;
  workspaceId: number;
  onUploadProgress: (percentUploaded: string) => void;
  onProcessingStarted: () => void;
  deferMarkReady?: boolean;
  dataGroupId?: string | null;
  groupKey?: string | null;
}): Promise<UploadSuccessResult | UploadFailedResult> {
  const {name: fileName, size: fileSize, type: fileType} = file;
  const maxMB = LOOKUP_TABLE_SIZE_PROJECT_OVERRIDES[projectId] ?? 100;
  const csvMaxFileSize = 1024 * 1024 * maxMB;

  if (fileSize > csvMaxFileSize) {
    return {succeeded: false, errorMessage: `Cannot upload files larger than ${maxMB} MB`};
  }

  try {
    const urlGetStartTime = getNow();
    const urlResults = await getLookupTableUploadUrl(projectId, workspaceId, fileType);

    const {url, path, key} = urlResults;
    const upload = new Upload({
      id: fileName,
      url,
      file,
      contentType: fileType,
      chunkSize: 256 * 2000 * 1024, // 500Mb chunks, must be a multiple of 256Kb.
      onChunkUpload: (info) => {
        onUploadProgress(formatPercent(info.uploadedBytes / info.totalBytes));
      },
    });
    const uploadStartTime = getNow();
    // throws on failure
    await upload.start();

    onProcessingStarted();
    const processStartTime = getNow();
    const processResults = await processLookupTable(
      projectId,
      workspaceId,
      groupKey || key,
      fileName,
      path,
      dataGroupId,
      deferMarkReady,
    );

    let results: ProcessLookupTableCompletedResults;
    if (`uploadId` in processResults) {
      // If we are uploading asynchronously poll until the table is ready.
      let complete = false;
      let polledTimes = 0;
      let sleepDuration;
      while (!complete) {
        sleepDuration = polledTimes > 5 ? 5000 : 500;
        await sleep(sleepDuration);
        const statusResp = await getLookupTableUploadStatus(projectId, workspaceId, processResults.uploadId);
        if (statusResp.uploadStatus === `SUCCESS`) {
          results = statusResp.result;
          complete = true;
          break;
        } else if (statusResp.uploadStatus === `FAILURE`) {
          return {succeeded: false, errorMessage: `Sorry, an error occurred. Please try importing again`};
        }
        polledTimes += 1;
      }
    } else {
      results = processResults;
    }

    if (`error` in results) {
      return {succeeded: false, errorMessage: `Unable to upload your file. ` + results.error};
    }

    const finishTime = getNow();
    // "deduped" refers to the number of rows remaining after dedupe (not the number deduped)
    const totalDropped = results.file - results.imported;
    const dedupeDropped = results.file - results.deduped;
    const nonDedupeDropped = results.deduped - results.imported;
    let droppedRowsWarningMessage, warningReason;
    if (nonDedupeDropped > 0) {
      warningReason = `Dropped rows (non-dedupe)`;
      droppedRowsWarningMessage = `${totalDropped} rows were dropped`;
    } else if (dedupeDropped > 0) {
      warningReason = `De-duplicated rows`;
      droppedRowsWarningMessage = `${dedupeDropped} rows were dropped as duplicate rows`;
    }

    return {
      succeeded: true,
      rowsUploaded: results.file,
      rowsImported: results.imported,
      ...(droppedRowsWarningMessage ? {droppedRowsWarningMessage} : {}),
      ...(warningReason ? {warningReason} : {}),
      dataGroupId: results.id,
      secondsToGetUrl: (uploadStartTime - urlGetStartTime) / 1000,
      secondsToUpload: (processStartTime - uploadStartTime) / 1000,
      secondsToProcess: (finishTime - processStartTime) / 1000,
      secondsTotal: (finishTime - urlGetStartTime) / 1000,
      numColumns: results.columns,
    };
  } catch (err) {
    reporter.error(err, `[lookup] Error uploading table`);
    return {succeeded: false, errorMessage: `Sorry, an error occurred. Please try importing again.`};
  }
}

export function markLookupTableReady(projectId: number, workspaceId: number, dataGroupId: string, fileName: string) {
  return apiClient.post(getLookupTablesBaseUrl(projectId, workspaceId), {
    'data-group-id': dataGroupId,
    'mark-ready': true,
    name: fileName,
  });
}
